import type { User } from 'oidc-client-ts';

/**
 * Whitelist test for a returnTo path read from OIDC state.
 * Returns true iff the value is a safe in-app pathname.
 *
 * Rules:
 *  - must start with '/'
 *  - must NOT start with '//' (protocol-relative URL escape)
 *  - must NOT start with '/auth/' (would re-enter the callback flow)
 *  - must NOT start with '/handler/' (OIDC handler routes)
 */
export function isSafeReturnTo(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value === '/auth' || value.startsWith('/auth/')) return false;
  if (value === '/handler' || value.startsWith('/handler/')) return false;
  return true;
}

/**
 * Pull a validated returnTo path off an OIDC user's state, or return '/' as fallback.
 */
export function extractReturnTo(user: User | null | undefined): string {
  const candidate = (user?.state as { returnTo?: unknown } | undefined)?.returnTo;
  return isSafeReturnTo(candidate) ? candidate : '/';
}
